import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  Banner,
  Button,
  Flex,
  Header,
  MultiSelect,
  OneColumnLayout,
  SelectField,
  Spacer,
  Text,
  spaces,
} from '@electricjs/arc';
import { usePageTitle } from '@/contexts/PageTitleContext';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { FormActionsContainer } from '@/components/Onboardings/common';
import { useListHrisProvidersQuery } from '@/redux/slices/hrisIntegrationApiSlice';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useOrganizationEmployees } from '@/hooks/useOrganizationEmployees';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import {
  HrisProviderOption,
  transformHrisProvidersToOptions,
} from '@/components/HRISIntegrations/transformOptions';
import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import type { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import { useTriggerDemoGroupChangeReviewMutation } from '@/redux/slices/groupApiSlice';
import { useAppSelector } from '@/redux/store';
import { useGetOrganizationGroupsForEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationGroupsQuery } from '@/redux/slices/organizationApiSlice';
import { Group } from '@/types/groups';

type GroupOption = {
  label: string;
  value: string;
};

type FormData = {
  hrisProvider: HrisProviderOption | null;
  employee: EmployeeOption | null;
  newGroup: GroupOption | null;
  removedGroups: GroupOption[];
};

const getFormSchema = (t: TFunction) =>
  yup.object().shape({
    hrisProvider: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().required(),
        name: yup.string().required(),
        label: yup.string().required(),
      })
      .required(t('pages:onboarding.hrisGroupChange.hrisProviderRequired')),
    employee: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
      .required(t('pages:onboarding.hrisGroupChange.employeeRequired')),
    newGroup: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
      .required(t('pages:onboarding.hrisGroupChange.newGroupRequired')),
  });

const NewHRISGroupChange = () => {
  const { t, i18n } = useTranslation(['pages', 'common', 'translation']);
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  // TODO: `groupChangeImpactAnalysisEnabled` is on in production for everyone and is likely
  // ready for removal.
  const { groupChangeImpactAnalysisEnabled } = useFlags();
  const titleProps = usePageTitle(t('pages:onboarding.hrisGroupChange.title'));
  const navigate = useNavigate();
  const organizationId = useGetOrganizationId();
  const { data: providers, isLoading: isLoadingProviders } =
    useListHrisProvidersQuery();
  const providerOptions = transformHrisProvidersToOptions(providers);

  const employees = useOrganizationEmployees();
  const employeeOptions = transformEmployeesToOptions(
    employees,
    i18n.language,
    isAdpWfnCanada
  );

  const [triggerGroupChangeReview] = useTriggerDemoGroupChangeReviewMutation();
  const userId = useAppSelector(state => state.loggedUser?.id) ?? '';
  const { showSuccessToast, showErrorToast } = useGlobalUI();

  const methods = useForm<FormData>({
    defaultValues: {
      hrisProvider: null,
      employee: null,
      newGroup: null,
      removedGroups: [],
    },
    mode: 'onBlur',
    resolver: yupResolver(getFormSchema(t)),
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid },
  } = methods;

  const selectedEmployeeOption = watch('employee');
  const selectedEmployeeId = selectedEmployeeOption?.value ?? '';

  useEffect(() => {
    setValue('newGroup', null);
    setValue('removedGroups', []);
  }, [selectedEmployeeId, setValue]);

  const { data: currentGroups = [], isLoading: isLoadingCurrentGroups } =
    useGetOrganizationGroupsForEmployeeQuery(
      { organizationId, employeeId: selectedEmployeeId },
      { skip: !selectedEmployeeId }
    );

  const currentGroupIds = new Set(currentGroups.map((g: Group) => g.id));

  const { data: allGroups = [], isLoading: isLoadingGroups } =
    useGetOrganizationGroupsQuery(
      { organizationId },
      { skip: !selectedEmployeeId }
    );

  const selectedNewGroupOption = watch('newGroup');
  const selectedNewGroupId = selectedNewGroupOption?.value;

  const newGroupOptions: GroupOption[] = allGroups
    .filter((g: Group) => !currentGroupIds.has(g.id))
    .map((g: Group) => ({ label: g.name, value: g.id }));

  const removedGroupOptions: GroupOption[] = currentGroups
    .filter((g: Group) => g.id !== selectedNewGroupId)
    .map((g: Group) => ({ label: g.name, value: g.id }));

  const handleErrorState = (logError?: string) => {
    showErrorToast({
      id: 'demo-group-change-error-toast',
      message: t('pages:onboarding.hrisGroupChange.errorMessage'),
      stack: true,
    });

    if (logError) {
      window?.DD_RUM?.addError(logError, {
        location: 'Create HCM Group Change',
      });
      console.error(logError);
    }
  };

  const submitGroupChange = async (data: FormData) => {
    try {
      const employeeId = data.employee?.value;
      if (!employeeId) {
        handleErrorState('Employee not selected');
        return;
      }

      const newGroupId = data.newGroup?.value;
      if (!newGroupId) {
        handleErrorState('New group not selected');
        return;
      }

      if (currentGroupIds.has(newGroupId)) {
        handleErrorState('Employee is already a member of the selected group');
        return;
      }

      await triggerGroupChangeReview({
        organizationId,
        employeeId,
        groupsAdded: [newGroupId],
        groupsRemoved: data.removedGroups.map(g => g.value),
        hrisProviderSlug: data.hrisProvider!.value,
        assigneeUserId: userId,
      }).unwrap();

      showSuccessToast({
        id: 'demo-group-change-success-toast',
        message: t('pages:onboarding.hrisGroupChange.successMessage'),
        stack: true,
      });

      navigate('/tasks');
    } catch (error) {
      handleErrorState(
        `Unable to submit group change: ${JSON.stringify(error)}`
      );
    }
  };

  return (
    <OneColumnLayout>
      <Flex maxWidth="72rem" width="100%" margin="0 auto" vertical>
        {!groupChangeImpactAnalysisEnabled && (
          <>
            <Banner
              id="banner-group-change-flag-disabled"
              intent="warning"
              width="100%"
              title={t(
                'pages:onboarding.hrisGroupChange.featureFlagDisabledTitle'
              )}
              message={t(
                'pages:onboarding.hrisGroupChange.featureFlagDisabledMessage'
              )}
            />
            <Spacer y="1.5rem" />
          </>
        )}
        <FormProvider {...methods}>
          <Header {...titleProps} />
          <Flex width="100%" vertical>
            <SelectField
              id="hris-providers-search-selector"
              name="hrisProvider"
              control={control}
              label={t('pages:onboarding.hrisGroupChange.hrisProviderLabel')}
              placeholder={t(
                'pages:onboarding.hrisGroupChange.hrisProviderPlaceholder'
              )}
              required
              isSearchable
              isClearable
              getOptionValue={provider => provider.id}
              getOptionLabel={provider => provider.name}
              options={providerOptions}
              isLoading={isLoadingProviders}
            />
            <SelectField<EmployeeOption>
              id="employee-search-select"
              name="employee"
              control={control}
              label={t('pages:onboarding.hrisGroupChange.employeeLabel')}
              placeholder={t(
                'pages:onboarding.hrisGroupChange.employeePlaceholder'
              )}
              required
              isSearchable
              isClearable
              getOptionValue={option => option.value}
              getOptionLabel={option => option.label}
              options={employeeOptions}
              customComponents={{
                Option: option => <EmployeeSelectOption data={option.data} />,
              }}
              filterOption={(option, inputValue) => {
                const search = inputValue.trim().toLowerCase();
                if (!search) return true;
                const data = option.data ?? option;
                const label = (option.label ?? data?.label ?? '').toLowerCase();
                const email = (data?.email ?? '').toLowerCase();
                const name = (data?.name ?? label).toLowerCase();
                return (
                  label.includes(search) ||
                  email.includes(search) ||
                  name.includes(search)
                );
              }}
            />
            {selectedEmployeeId && currentGroups.length > 0 && (
              <Flex vertical mb="1rem">
                <Text variant="label-large" mb="0.5rem">
                  {t('pages:onboarding.hrisGroupChange.currentGroupsLabel')}
                </Text>
                <Text variant="body-2">
                  {currentGroups.map((g: Group) => g.name).join(', ')}
                </Text>
              </Flex>
            )}
            {selectedEmployeeId && (
              <SelectField<GroupOption>
                id="new-group-select"
                name="newGroup"
                control={control}
                label={t('pages:onboarding.hrisGroupChange.newGroupLabel')}
                placeholder={t(
                  'pages:onboarding.hrisGroupChange.newGroupPlaceholder'
                )}
                required
                isSearchable
                isClearable
                getOptionValue={option => option.value}
                getOptionLabel={option => option.label}
                options={newGroupOptions}
                isLoading={isLoadingGroups || isLoadingCurrentGroups}
              />
            )}
            {selectedEmployeeId && currentGroups.length > 0 && (
              <Flex vertical mb="1rem">
                <Text
                  variant="label-input"
                  display="block"
                  mb={spaces.space_xs_rem}>
                  {t('pages:onboarding.hrisGroupChange.removedGroupsLabel')}
                </Text>
                <Controller
                  name="removedGroups"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect<GroupOption>
                      id="removed-groups-select"
                      isSearchable
                      isClearable
                      getOptionValue={option => option.value}
                      getOptionLabel={option => option.label}
                      options={removedGroupOptions}
                      isLoading={isLoadingGroups}
                      placeholder={t(
                        'pages:onboarding.hrisGroupChange.removedGroupsPlaceholder'
                      )}
                      value={field.value}
                      onChange={values => field.onChange([...values])}
                    />
                  )}
                />
              </Flex>
            )}
          </Flex>
          <FormActionsContainer>
            <Button
              id="demo-group-change-cancel-button"
              variant="outline"
              onClick={() => navigate(-1)}>
              {t('pages:onboarding.hrisGroupChange.cancel')}
            </Button>
            <Button
              id="demo-group-change-submit-button"
              onClick={handleSubmit(submitGroupChange)}
              disabled={
                !isValid ||
                isLoadingCurrentGroups ||
                !groupChangeImpactAnalysisEnabled
              }>
              {t('pages:onboarding.hrisGroupChange.submit')}
            </Button>
          </FormActionsContainer>
        </FormProvider>
      </Flex>
    </OneColumnLayout>
  );
};

export default NewHRISGroupChange;
